<template>
  <div class="navigation-bar-notifications" v-closable="closePanel" @keyup.esc="onEsc">
    <button
      class="navigation-bar-notifications__bell"
      :aria-label="$t('notifications-center.title')"
      @click="togglePanel"
    >
      <BaseIcon name="bell" />
      <BaseNotification v-if="unreadCount > 0" class="navigation-bar-notifications__badge" :amount="unreadCount" />
    </button>

    <div v-if="isOpen" class="navigation-bar-notifications__panel">
      <NotificationsHeader
        class="navigation-bar-notifications__header"
        :unread-count="unreadCount"
        @mark-all="markAllAsRead"
      />

      <NotificationEventList
        class="navigation-bar-notifications__body"
        :events="events"
        :pending="pending"
        :competitors="competitors"
        @open="onOpen"
        @mark-read="onMarkRead"
      />
      <div class="navigation-bar-notifications__footer">
        <BaseButton
          v-if="hasMoreEvents"
          class="navigation-bar-notifications__see-more"
          is-transparent
          is-square
          is-full-width
          @click.native="openDialog"
        >
          {{ $t('notifications-center.see-more', { count: remainingEventsCount }) }}
        </BaseButton>
      </div>
    </div>

    <NotificationsDialog v-if="dialogOpen" @close-dialog="dialogOpen = false" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BaseNotification from '@/components/base/BaseNotification.vue';
import NotificationEventList from '@/components/notifications-center/NotificationEventList.vue';
import NotificationsDialog from '@/components/notifications-center/NotificationsDialog.vue';
import NotificationsHeader from '@/components/notifications-center/NotificationsHeader.vue';
import notificationOpenMixin from '@/mixins/notification-open-mixin';
import { viewport } from '@/util/viewport';

export const POLL_INTERVAL_MS = 5 * 60 * 1000;

export default {
  name: 'NavigationBarNotifications',
  components: {
    BaseButton,
    BaseIcon,
    BaseNotification,
    NotificationEventList,
    NotificationsDialog,
    NotificationsHeader,
  },
  mixins: [notificationOpenMixin],
  data() {
    return {
      isOpen: false,
      dialogOpen: false,
      hasLoadedCompetitors: false,
      pollTimer: null,
    };
  },
  computed: {
    ...mapGetters('notifications', ['events', 'unreadCount', 'pending', 'hasMoreEvents', 'remainingEventsCount']),
    ...mapGetters('biDashboard', ['competitors']),
  },
  watch: {
    $route() {
      this.closePanel();
    },
  },
  created() {
    this.getEvents();

    this.pollTimer = setInterval(this.getEvents, POLL_INTERVAL_MS);
  },
  beforeDestroy() {
    clearInterval(this.pollTimer);
  },
  methods: {
    ...mapActions('notifications', ['getEvents', 'markAllAsRead']),
    ...mapActions('biDashboard', ['getAllCompetitors']),
    togglePanel() {
      if (viewport.size === 'phone') {
        // Already there: the rejected navigation is no error.
        this.$router.push({ name: 'notifications' }).catch(() => {});
        return;
      }

      if (this.isOpen) {
        this.closePanel();
      } else {
        this.openPanel();
      }
    },
    openPanel() {
      this.isOpen = true;

      if (!this.hasLoadedCompetitors && this.competitors.length === 0) {
        this.hasLoadedCompetitors = true;
        this.getAllCompetitors();
      }
    },
    closePanel() {
      this.isOpen = false;
    },
    onEsc(event) {
      if (!this.isOpen) return;

      event.stopPropagation();
      this.closePanel();
    },
    openDialog() {
      this.closePanel();
      this.dialogOpen = true;
    },
    onMarkRead(notification) {
      this.markAsRead(notification.id);
    },
    onOpen(notification) {
      this.openNotification(notification);
      this.closePanel();
    },
  },
};
</script>

<style lang="scss" scoped>
.navigation-bar-notifications {
  position: relative;

  &__bell {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;

    .base-icon {
      width: 24px;
      height: 24px;
      color: $color-normal;
      transition: color 200ms ease;
    }

    &:hover .base-icon {
      color: $color-accent;
    }
  }

  &__badge {
    position: absolute;
    top: -8px;
    right: -8px;
  }

  &__panel {
    position: absolute;
    top: 40px;
    right: 0;
    z-index: $nav-bar-index;
    display: flex;
    flex-direction: column;
    width: 420px;
    padding: $space-md;
    border-radius: $radius-xl;
    background: $color-white;
    box-shadow: $shadow-dropdown;

    @media screen and (max-width: 767px) {
      position: fixed;
      top: $menu-top-bar-height;
      right: $space-sm;
      left: $space-sm;
      width: auto;
    }
  }

  &__header {
    padding: 4px 4px 8px 4px;
  }

  &__footer {
    padding-top: 8px;
  }

  &__body {
    max-height: 65vh;
    overflow-y: auto;
    padding-right: 16px;
  }

  &__see-more {
    ::v-deep .base-button__element {
      color: $color-accent-500;
      font-size: $font-size-small;
      font-weight: $font-weight-bold;

      &:hover,
      &:active {
        background-color: $color-accent-100;
        color: $color-accent-500;
      }

      &:focus {
        // BaseButton's base :focus rule sets color: $color-white — invisible on a transparent background.
        color: $color-accent-500;
      }
    }
  }
}
</style>
